import React, { useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { Bounce, toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import EmptyShipping from "./components/EmptyShipping";
import AddShipping from "./components/AddShipping";
import ShippingPlans from "./components/ShippingPlans";
import DeletePopup from "./components/DeletePopup";
import ShippingSkeleton from "./components/ShippingSkeleton";

import "./assets/css/shipping.css";

const App = () => {
    const [addNewShipping, setAddNewShipping] = useState(false);
    const [shippingPlans, setShippingPlans] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showDeletePopup, setShowDeletePopup] = useState(false);
    const [shippingPlanIdToDelete, setShippingPlanIdToDelete] = useState(null);
    const [shippingPlanIdToEdit, setShippingPlanIdToEdit] = useState(null);

    const showToast = (type, message) => {
        toast[type](message, {
            position: "top-right",
            style: {
                margin: "30px 0 0 0",
                fontSize: "16px",
                fontWeight: "500",
                lineHeight: "26px",
                color: "#fff",
            },
            autoClose: 1500,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: false,
            progress: undefined,
            theme: "colored",
            transition: Bounce,
        });
    };

    const handleDeleteShipping = () => {
        if (!shippingPlanIdToDelete) return;

        setShowDeletePopup(false);
        easycommerce_modal(true);

        fetch(
            `${EASYCOMMERCE.rest_base}/shipping-plans/${shippingPlanIdToDelete}`,
            {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json",
                    "X-WP-Nonce": EASYCOMMERCE.nonce,
                },
            }
        )
            .then((res) => res.json())
            .then((data) => {
                easycommerce_modal(false);

                if (data.success) {
                    setShippingPlans(
                        shippingPlans.filter(
                            (plan) => plan.id !== shippingPlanIdToDelete
                        )
                    );

                    showToast("success", data.data);
                    setShippingPlanIdToDelete(null);
                } else {
                    showToast("error", data.data);
                    setShippingPlanIdToDelete(null);
                }
            });
    };

    useEffect(() => {
        if (addNewShipping) return;

        setIsLoading(true);
        fetch(`${EASYCOMMERCE.rest_base}/shipping-plans`,{
            headers: {
                "Content-Type": "application/json",
                "X-WP-Nonce": EASYCOMMERCE.nonce,
            }
        })
            .then((res) => res.json())
            .then((data) => {
                setIsLoading(false);
                if (data.success && data.data?.plans) {
                    setShippingPlans(data.data.plans);
                }
            });
    }, [addNewShipping]);

    return (
        <>
            <div className="flex justify-start items-center gap-8 h-12">
                <p className="text-ec-body font-semibold font-inter lg:text-xl md:text-lg leading-8">
                    {!addNewShipping ? "Shipping List" : "Create Shipping"}
                </p>
                {!addNewShipping && (
                    <button
                        onClick={() => setAddNewShipping(true)}
                        className="w-[150px] h-12 flex justify-center items-center gap-[8px] font-inter 
                        bg-white group border border-ec-primary rounded-lg text-ec-primary 
                        hover:text-white hover:bg-ec-primary focus:shadow-none focus:text-white 
                        focus:bg-ec-secondary lg:text-sm md:text-xs sm:text-sm transition-all 
                        ease-in-out duration-500"
                    >
                        <svg
                            class="w-4 h-4 font-medium"
                            data-slot="icon"
                            fill="none"
                            stroke-width="1.5"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                            aria-hidden="true"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                d="M12 4.5v15m7.5-7.5h-15"
                            ></path>
                        </svg>
                        New Shipping
                    </button>
                )}
            </div>

            {!isLoading ? (
                <div className="easycommerce-shipping">
                    {shippingPlans.length === 0 ? (
                        <>
                            {addNewShipping ? (
                                <AddShipping
                                    handleCancel={() => {
                                        setAddNewShipping(false);
                                        setShippingPlanIdToEdit(null);
                                    }}
                                    showToast={showToast}
                                    ShippingPlanId={shippingPlanIdToEdit}
                                />
                            ) : (
                                <EmptyShipping
                                    handleAddNew={() => setAddNewShipping(true)}
                                />
                            )}
                        </>
                    ) : (
                        <>
                            {addNewShipping ? (
                                <AddShipping
                                    handleCancel={() => {
                                        setAddNewShipping(false);
                                        setShippingPlanIdToEdit(null);
                                    }}
                                    showToast={showToast}
                                    ShippingPlanId={shippingPlanIdToEdit}
                                />
                            ) : (
                                <ShippingPlans
                                    shippingPlans={shippingPlans}
                                    handleShippingEdit={(id) => {
                                        setAddNewShipping(true);
                                        setShippingPlanIdToEdit(id);
                                    }}
                                    handleDeleteShipping={(id) => {
                                        setShowDeletePopup(true);
                                        setShippingPlanIdToDelete(id);
                                    }}
                                />
                            )}
                        </>
                    )}
                </div>
            ) : (
                <ShippingSkeleton />
            )}

            {showDeletePopup && (
                <DeletePopup
                    discardAction={() => {
                        setShowDeletePopup(false);
                        setShippingPlanIdToDelete(null);
                    }}
                    deleteAction={handleDeleteShipping}
                />
            )}

            <ToastContainer />
        </>
    );
};

const container = document.getElementById("easycommerce-shipping-methods");
const root = createRoot(container);
root.render(<App />);

export default App;
